import { FormEvent, useState } from "react";
import { Navigate, useNavigate } from "@entry/navigation";
import styled from "styled-components";

import { AnalyticsEvent, Routes, StorageKey } from "@ctypes";
import { useEntryIntakeStore } from "@entry/store";
import { EntryBody, EntryCta, EntryTitle, GlassInput } from "@entry/ui";
import { useEntryAnimation } from "@entry/useEntryAnimation";
import { useErrorBanner, useSetUserIdentity } from "@hooks";
import { Translations, i18n } from "@i18n";
import { Analytics } from "@services";
import { Screen, ScreenColumn, ScreenCta, useCtaSpacer, useEntryPageAnalytics } from "./shared";

const Fields = styled.div`
  margin-top: 30px;
`;

export const PreferredNameScreen = () => {
  const navigate = useNavigate();
  const { intake, setIntake } = useEntryIntakeStore();

  const [preferredName, setPreferredName] = useState(intake.preferredName || intake.firstName);

  // Phone-first signup runs this intake after login, so there is no registration step left to
  // carry the name to the backend. The whole name goes up here rather than at the name screen
  // because the missing-field router stops offering these screens once firstName exists.
  const isAuthenticated = Boolean(localStorage.getItem(StorageKey.Token));

  const [setUserIdentity, { loading }] = useSetUserIdentity();
  const { showErrorBanner } = useErrorBanner();

  useEntryPageAnalytics(Routes.EntryPreferredName);

  const { contentStyle, formStyle, ctaStyle, animateOut } = useEntryAnimation();
  const { ctaRef, ctaSpacerHeight } = useCtaSpacer();

  if (!intake.firstName) {
    return <Navigate to={Routes.EntryName} replace />;
  }

  const trimmed = preferredName.trim();
  const canContinue = trimmed.length > 0;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canContinue || loading) return;

    Analytics.track(AnalyticsEvent.EntryPreferredNamePrimaryCTAPressed);

    if (isAuthenticated) {
      try {
        await setUserIdentity({
          variables: {
            ...(intake.firstName && { firstName: intake.firstName }),
            ...(intake.lastName && { lastName: intake.lastName }),
            preferredName: trimmed,
          },
        });
      } catch {
        showErrorBanner();
        return;
      }
    }

    setIntake({ preferredName: trimmed });
    animateOut(() => navigate(Routes.EntryGreeting));
  };

  return (
    <Screen>
      <ScreenColumn as="form" onSubmit={handleSubmit} noValidate>
        <div style={{ ...contentStyle, marginTop: 0 }}>
          <EntryTitle $size={36}>{i18n.t(Translations.EntryPreferredNameTitle)}</EntryTitle>
          <EntryBody style={{ marginTop: 18 }}>
            {i18n.t(Translations.EntryPreferredNameSubtitle)}
          </EntryBody>
        </div>

        <div style={formStyle}>
          <Fields>
            <GlassInput
              id="entry-preferred-name"
              name="nickname"
              autoComplete="nickname"
              label={i18n.t(Translations.EntryPreferredNameInput)}
              value={preferredName}
              autoFocus
              onChange={(event) => setPreferredName(event.target.value)}
            />
          </Fields>
        </div>

        <div aria-hidden="true" style={{ height: ctaSpacerHeight, flexShrink: 0 }} />
        <ScreenCta ref={ctaRef} style={ctaStyle}>
          <EntryCta
            type="submit"
            $variant="green"
            $disabled={!canContinue || loading}
            disabled={!canContinue || loading}
            aria-disabled={!canContinue || loading}
          >
            {canContinue
              ? i18n.t(Translations.EntryPreferredNameCTA, {
                  preferredName: trimmed,
                })
              : i18n.t(Translations.EntryNamePrimaryCTA)}
          </EntryCta>
        </ScreenCta>
      </ScreenColumn>
    </Screen>
  );
};
